import { requestTemplate } from 'api';
import { AxiosProgressEvent, CancelToken } from 'axios';
import { DocumentFilterOption, IDetails, IDocument, IDocumentInfo, IFolder, ILocale, IRecycleBinDocument, IRecycleBinFolder, IResearchResponce } from 'interfaces';

type DeleteResponce = {
  success: boolean,
  detail: string,
}

type DocumentsPayload = {
  search?: string;
  folder_id?: string;
  room_id: string;
  action?: string;
  filters?: string;
}

type CreateEmptyDocumentPayload = {
  room_id: string;
  folder_id: string | null;
  document_name: string;
  extension: 'docx' | 'xlsx' | 'pptx';
}

//DOCUMENTS
const getDocuments = (payload: DocumentsPayload, sourceToken?: CancelToken) =>
  requestTemplate.get<IDocument[]>(`/documents/`, {
    cancelToken: sourceToken,
    params: payload,
  }
);

const getDocumentFilters = (room_id: string) =>
  requestTemplate.get<DocumentFilterOption[]>('/documents/filters/', { params: { room_id } });

const searchDocsAnFolders = (payload: DocumentsPayload, sourceToken?: CancelToken) =>
  requestTemplate.get<{documents: IDocument[], folders: IFolder[]}>(`/documents/search/`, {
    cancelToken: sourceToken,
    params: payload,
  }
);


const changeAgreementStatus = <T = any> (
  payload: {id: string, entity: string}, room_id: string, entity_id: string
) => requestTemplate.put<T>(`/documents/agreement/?action=update&id=${room_id}&entity=${entity_id}`, payload);

const uploadFile = (
  payload: { formData: FormData, id: string, name: string, action: string, signal?: AbortSignal, onUploadProgress?: (progressEvent: AxiosProgressEvent)=> void},
) => requestTemplate.post<IDocument>(`/documents/upload/?action=${payload.action}&id=${payload.id}&document_name=${payload.name}`, payload.formData, {
   headers: { 'Accept': 'application/json', 'Content-Type': 'multipart/form-data' },
   onUploadProgress: payload.onUploadProgress,
   signal: payload.signal
});

const deleteDocuments = (
  payload: string[], logInfo: IDetails
) => requestTemplate.delete<DeleteResponce>(`/documents/?action=delete&id=${logInfo.room_id}`,  { data: {documents_ids: payload, details: logInfo.details, include_versions: logInfo.include_versions } });

const getDocumentById = (document_id: string) => requestTemplate.get<IDocument>(`/documents/by_id/?document_id=${document_id}`);

const getFolderById = (folder_id: string, room_id: string) => requestTemplate.get<IFolder>(`/folders/by_id/?folder_id=${folder_id}&room_id=${room_id}`);

const downloadFile = (payload: any) => requestTemplate.get<Blob>(`/documents/download/`, { responseType: 'blob', params: payload });

const viewFile = (payload: any) => requestTemplate.get<Blob>(`/documents/view/`, { responseType: 'blob', params: payload });

const getVideoLink = (payload: {token: string, is_temp_links?: boolean}) => requestTemplate.get<string>(`/documents/link/`, {params: payload});

const convertRequest = (payload: IDocumentInfo & ILocale, signal?: AbortSignal) => requestTemplate.post(`/documents/convert/pdf/?token=${payload.token}`, payload, {
  signal: signal
});

const getConvertedFile = (payload: IDocumentInfo & ILocale, signal?: AbortSignal) => requestTemplate.post<Blob>(`/documents/pdf/?token=${payload.token}`,payload,{
  responseType: 'blob',
  signal: signal
},);

// const getVideoManifest = (token: string) => requestTemplate.get<string>(`/documents/manifest/?token=${token}`);

const researchDocument = (payload: FormData) =>
  requestTemplate.post<IResearchResponce>('/documents/research/', payload, {
    headers: { Accept: 'application/json', 'Content-Type': 'multipart/form-data' },
  });
  
const declineAgreement = (payload: { comment: string; id: string; entity: string }) =>
  requestTemplate.post('/documents/agreement/decline/', payload);

const downloadAsArchive = (payload: string) => requestTemplate.get(`/documents/download_archive/?room_id=${payload}`, {
  responseType: 'blob',
});

const bulkDownloadArchive = (payload: { document_ids?: string[], folder_ids?: string[] }) =>
  requestTemplate.post<Blob>('/documents/bulk-download/', payload, {
    responseType: 'blob',
  });

const hasDuplicate = (payload: { document_name: string; room_id: string }) =>
  requestTemplate.get<IDocument[]>('/documents/namesakes/', {params: payload});
const validateDocuments = (payload: { document_name: string; room_id: string }) =>
  requestTemplate.get<{generated_name: string, documents: IDocument[]}>('/documents/validate-name/', {params: payload});

const createEmptyDocument = (payload: CreateEmptyDocumentPayload) =>
  requestTemplate.post<IDocument>('/documents/create/', payload);

const getAllDocumentsOfRoom = (room_id: string) =>
  requestTemplate.get<IDocument[]>(`/documents/all/?room_id=${room_id}`);

const forceDeleteDocuments = (payload: { room_id: string; documents_ids: string[] }) =>
  requestTemplate.delete<DeleteResponce>('/documents/force/', { data: payload });

const getRecycleBinDocuments = (room_id: string) =>
  requestTemplate.get<IRecycleBinDocument[]>('/recycle_bin/documents/', { params: { room_id } });

const deleteRecycleBinDocuments = (payload: { room_id: string; documents_ids: string[] }) =>
  requestTemplate.delete<DeleteResponce>('/recycle_bin/documents/', { data: payload });

const restoreRecycleBinDocuments = (payload: { room_id: string; documents_ids: string[] }) =>
  requestTemplate.post<DeleteResponce>('/recycle_bin/documents/restore/', payload);

const restoreRecycleBinDocumentsInRoot = (payload: { room_id: string; documents_ids: string[] }) =>
  requestTemplate.post<DeleteResponce>('/recycle_bin/documents/restore-in-root/', payload);

const convertTempLinkRequest = (payload: {
  temp_link_id: string;
  password?: string;
  is_agreement: boolean;
}) => requestTemplate.post<{status: string, token: string} | {message: string, success: boolean}>(
  `/documents/temp-link/convert/pdf/`, 
  payload
);

const documents = { 
  getDocuments,
  getDocumentFilters,
  searchDocsAnFolders,
  changeAgreementStatus,
  uploadFile,
  deleteDocuments,
  downloadFile,
  viewFile,
  getVideoLink,
  convertRequest,
  getConvertedFile,
  getDocumentById,
  getFolderById,
  researchDocument,
  declineAgreement,
  downloadAsArchive,
  bulkDownloadArchive,
  hasDuplicate,
  getAllDocumentsOfRoom,
  validateDocuments,
  createEmptyDocument,
  convertTempLinkRequest,
  forceDeleteDocuments,
  getRecycleBinDocuments,
  deleteRecycleBinDocuments,
  restoreRecycleBinDocuments,
  restoreRecycleBinDocumentsInRoot,
};

export default documents;
